import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ImagePlus, X, Sparkles } from "lucide-react";

const LETTERS = ["A", "B", "C", "D"];

export default function AdminQuestionNew() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [textPt, setTextPt] = useState("");
  const [opts, setOpts] = useState({ A: "", B: "", C: "", D: "" });
  const [correct, setCorrect] = useState("A");
  const [explanation, setExplanation] = useState("");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("");

  useEffect(() => { api.get("/admin/categories").then((r) => setCats(r.data)); }, []);

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Only JPEG, PNG or WebP images are allowed");
      return;
    }
    setImage(f);
    setPreview(URL.createObjectURL(f));
  };

  const clearImage = () => {
    setImage(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    const options_pt = LETTERS.filter((l) => opts[l].trim()).map((l) => ({ letter: l, text: opts[l].trim() }));
    if (!cat) { toast.error("Choose a category"); return; }
    if (textPt.trim().length < 5) { toast.error("Question text is too short"); return; }
    if (!opts.A.trim() || !opts.B.trim()) { toast.error("Options A and B are required"); return; }
    if (!options_pt.some((o) => o.letter === correct)) { toast.error(`Correct option ${correct} has no text`); return; }

    setBusy(true);
    try {
      setPhase("Creating question · generating EN/UR translations & PT audio…");
      const r = await api.post("/admin/questions", {
        category_code: cat,
        text_pt: textPt.trim(),
        options_pt,
        correct_letter: correct,
        explanation_pt: explanation.trim() || null,
      });
      const { id, generated } = r.data;
      if (image) {
        setPhase("Uploading image…");
        const fd = new FormData();
        fd.append("file", image);
        try {
          await api.post(`/admin/questions/${id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (e) {
          toast.error("Question created, but the image upload failed — retry from the edit page");
        }
      }
      const failed = ["audio", "en", "ur"].filter((k) => !generated[k]);
      if (failed.length === 0) {
        toast.success("Question created with translations and audio");
      } else {
        toast.warning(`Question created, but generation failed for: ${failed.join(", ").toUpperCase()} — regenerate from the edit page`);
      }
      nav(`/admin/questions/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); setPhase(""); }
  };

  return (
    <AdminLayout title="New question" actions={
      <>
        <Button variant="ghost" onClick={() => nav("/admin/questions")} className="text-white/70" data-testid="back-questions" disabled={busy}>
          <ArrowLeft className="w-4 h-4 mr-1" /> All questions
        </Button>
        <Button onClick={submit} disabled={busy} className="bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90" data-testid="create-question">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
          {busy ? "Creating…" : "Create question"}
        </Button>
      </>
    }>
      {busy && (
        <div className="mb-5 flex items-center gap-3 bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/30 rounded-lg px-4 py-3 text-sm" data-testid="create-progress">
          <Loader2 className="w-4 h-4 animate-spin text-[hsl(var(--accent))]" />
          <span>{phase} <span className="text-white/50">This can take ~20 seconds.</span></span>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white/4 border border-white/10 rounded-lg p-6">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/50 font-mono">Category</Label>
                <Select value={cat} onValueChange={setCat}>
                  <SelectTrigger className="bg-white/5 border-white/15 text-white mt-1.5" data-testid="new-q-category">
                    <SelectValue placeholder="Choose category…" />
                  </SelectTrigger>
                  <SelectContent>
                    {cats.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} · {c.name_pt}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/50 font-mono">Correct letter</Label>
                <div className="flex gap-1.5 mt-1.5">
                  {LETTERS.map((l) => (
                    <button key={l} onClick={() => setCorrect(l)} data-testid={`new-q-correct-${l}`} type="button"
                      className={`flex-1 h-10 rounded-md font-mono font-semibold border transition-colors ${
                        correct === l ? "bg-[hsl(var(--accent))] text-black border-transparent" : "bg-white/5 border-white/15 text-white/70 hover:border-white/40"
                      }`}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mb-4">
              <Label className="text-xs uppercase tracking-widest text-white/50 font-mono">Question text (PT)</Label>
              <Textarea value={textPt} onChange={(e) => setTextPt(e.target.value)} placeholder="Escreva a pergunta em portugues…"
                className="bg-white/5 border-white/15 text-white mt-1.5 min-h-24" data-testid="new-q-text" />
            </div>
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-widest text-white/50 font-mono">Options (PT) — A &amp; B required, C &amp; D optional</Label>
              {LETTERS.map((l) => (
                <div key={l} className="flex gap-3 items-start">
                  <div className={`font-mono font-semibold w-8 h-9 flex items-center justify-center rounded-md ${correct === l ? "bg-emerald-500/20 text-emerald-400" : "bg-white/5 text-white/60"}`}>{l}</div>
                  <Textarea value={opts[l]} onChange={(e) => setOpts({ ...opts, [l]: e.target.value })}
                    className="bg-white/5 border-white/15 text-white flex-1 min-h-16" data-testid={`new-q-opt-${l}`} />
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Label className="text-xs uppercase tracking-widest text-white/50 font-mono">Explanation (PT, optional)</Label>
              <Textarea value={explanation} onChange={(e) => setExplanation(e.target.value)}
                className="bg-white/5 border-white/15 text-white mt-1.5" data-testid="new-q-explanation" />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white/4 border border-white/10 rounded-lg p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3">// illustration (optional)</div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pickImage} data-testid="new-q-image-input" />
            {preview ? (
              <div className="relative">
                <img src={preview} alt="preview" className="rounded border border-white/10 max-h-48 w-full object-contain bg-black/30" data-testid="new-q-image-preview" />
                <button onClick={clearImage} data-testid="new-q-image-clear"
                  className="absolute top-2 right-2 bg-black/70 rounded-full p-1 text-white/80 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Button variant="outline" onClick={() => fileRef.current?.click()} className="w-full text-white bg-white/5 border-white/15" data-testid="new-q-image-pick">
                <ImagePlus className="w-4 h-4 mr-2" /> Choose image
              </Button>
            )}
            <p className="text-[11px] text-white/40 mt-2">JPEG, PNG or WebP, max 5 MB.</p>
          </div>

          <div className="bg-white/4 border border-white/10 rounded-lg p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-2">// automatic on create</div>
            <ul className="text-sm text-white/70 space-y-1.5 list-disc list-inside">
              <li>English translation (Claude)</li>
              <li>Roman Urdu translation (Claude)</li>
              <li>Portuguese audio (OpenAI TTS)</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
